import { Building2, Calendar, Briefcase, MapPin, Clock, TrendingUp, Zap, BarChart3, Info, Plus, Edit3, Search, GraduationCap, Trash2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AddExperienceModal } from "./AddExperienceModal";
import { experienceStorage, ExperienceEntry } from "@/utils/experienceStorage";
import { useSettings } from "@/providers/SettingsProvider";
import { formatExperienceLastUsed } from "@/utils/dateFormatter";
import { useCandidateExperiences } from "@/hooks/candidate/useCandidateExperiences";
import { calculateYearsOfExperience, formatDuration, formatDateRange, getYear } from "@/utils/experienceDuration";

import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";



interface CandidateExperienceTabProps {
  candidateId: string;
  aiAnalysis: any;
}

export const CandidateExperienceTab = ({ candidateId, aiAnalysis }: CandidateExperienceTabProps) => {
  const { t } = useTranslation();
  const { outdatedThreshold, setOutdatedThresholdValue } = useSettings();
  
  // Fetch experiences and skills from database
  const { experiences: dbExperiences, skills: dbSkills, loading: dbLoading } = useCandidateExperiences(candidateId);
  
  // State for local experiences and modal
  const [localExperiences, setLocalExperiences] = useState<ExperienceEntry[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingExperience, setEditingExperience] = useState<ExperienceEntry | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [jobsFilter, setJobsFilter] = useState("");
  const [skillsFilter, setSkillsFilter] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [experienceToDelete, setExperienceToDelete] = useState<ExperienceEntry | null>(null);

  // Load local experiences on mount
  useEffect(() => {
    const loadExperiences = async () => {
      try {
        const stored = await experienceStorage.getExperiences(candidateId);
        setLocalExperiences(stored);
      } catch (error) {
        console.error('Error loading experiences:', error);
        toast.error('Failed to load experiences');
      }
    };
    
    loadExperiences();
  }, [candidateId]);

  // Transform database experiences to match the component's expected format
  const transformedDbExperiences = dbExperiences.map(exp => {
    // Find skills for this experience by matching context with company name
    const experienceSkills = dbSkills
      .filter(skill => {
        if (!skill.context) return false;
        // Check if the skill's context mentions this company
        return skill.context.includes(exp.company);
      })
      .map(skill => skill.name);

    return {
      role: exp.role,
      company: exp.company,
      dates: formatDateRange(exp.start_date, exp.end_date),
      skills: experienceSkills,
      isLocallyAdded: false,
      start_date: exp.start_date,
      end_date: exp.end_date,
      is_current: exp.is_current,
    };
  });

  // Combine database and local experiences, marking which ones are locally added
  const experienceData = [
    ...transformedDbExperiences,
    ...localExperiences.map(exp => ({ ...exp, isLocallyAdded: true }))
  ];

  // Handler functions
  const handleAddExperience = async (experience: ExperienceEntry) => {
    setIsLoading(true);
    try {
      if (editingIndex !== null && editingExperience?.id) {
        // Update existing experience
        await experienceStorage.updateExperience(editingExperience.id, experience);
        toast.success("Experience updated successfully!");
      } else {
        // Add new experience
        await experienceStorage.addExperience(candidateId, experience);
        toast.success("Experience added successfully!");
      }
      
      const updated = await experienceStorage.getExperiences(candidateId);
      setLocalExperiences(updated);
      setIsAddModalOpen(false);
      setEditingExperience(null);
      setEditingIndex(null);
    } catch (error) {
      console.error("Error saving experience:", error);
      toast.error("Failed to save experience. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditExperience = (experience: ExperienceEntry, index: number) => {
    // Only allow editing locally added experiences
    if (experience.isLocallyAdded && experience.id) {
      setEditingExperience(experience);
      setEditingIndex(0); // Index not really used anymore since we have the ID
      setIsAddModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingExperience(null);
    setEditingIndex(null);
  };

  const handleDeleteExperience = async () => {
    if (!experienceToDelete?.id) return;
    
    setIsLoading(true);
    try {
      await experienceStorage.removeExperience(experienceToDelete.id);
      toast.success(t("experiences.deleteSuccess", "Experience deleted successfully!"));
      
      // Reload experiences
      const updated = await experienceStorage.getExperiences(candidateId);
      setLocalExperiences(updated);
      
      setShowDeleteDialog(false);
      setExperienceToDelete(null);
    } catch (error) {
      console.error("Error deleting experience:", error);
      toast.error(t("experiences.deleteError", "Failed to delete experience. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = (experience: ExperienceEntry) => {
    setExperienceToDelete(experience);
    setShowDeleteDialog(true);
  };

  // Filter function that's case and order insensitive
  const filterItems = (items: [string, number][], filterText: string) => {
    if (!filterText.trim()) return items;
    
    const filterWords = filterText.toLowerCase().split(' ').filter(word => word.length > 0);
    
    return items.filter(([itemName]) => {
      const itemNameLower = itemName.toLowerCase();
      // Check if all filter words are present in the item name (order doesn't matter)
      return filterWords.every(word => itemNameLower.includes(word));
    });
  };

  // Helper function to parse dates and calculate duration
  const parseDateRange = (dateString: string, experience?: any) => {
    // If experience has start_date and end_date from database, use those
    if (experience?.start_date) {
      const startYear = getYear(experience.start_date);
      const endYear = getYear(experience.end_date);
      const years = calculateYearsOfExperience(experience.start_date, experience.end_date);
      const duration = formatDuration(experience.start_date, experience.end_date);
      const isPresent = experience.is_current || !experience.end_date;
      
      return {
        start: formatDateRange(experience.start_date, experience.end_date).split(' - ')[0],
        end: isPresent ? "Present" : formatDateRange(experience.start_date, experience.end_date).split(' - ')[1],
        duration,
        isPresent,
        startYear,
        endYear,
        years,
      };
    }
    
    // Fallback to string parsing for locally added experiences
    const cleanDate = dateString.trim();
    const dateWithoutDuration = cleanDate.replace(/\s*\([^)]*\)\s*$/, '');
    const parts = dateWithoutDuration.split(' - ');
    
    if (parts.length === 2) {
      const startDate = parts[0].trim();
      const endDate = parts[1].trim().toUpperCase();
      
      let startYear = 0;
      if (startDate.includes('/')) {
        const dateParts = startDate.split('/');
        startYear = parseInt(dateParts[dateParts.length - 1]) || 0;
      } else {
        const yearMatch = startDate.match(/\b(20\d{2})\b/);
        if (yearMatch) {
          startYear = parseInt(yearMatch[1]) || 0;
        } else {
          startYear = parseInt(startDate) || 0;
        }
      }
      
      let endYear = 0;
      if (endDate === "PRESENT") {
        endYear = new Date().getFullYear();
      } else if (endDate.includes('/')) {
        const dateParts = endDate.split('/');
        endYear = parseInt(dateParts[dateParts.length - 1]) || 0;
      } else {
        const yearMatch = endDate.match(/\b(20\d{2})\b/);
        if (yearMatch) {
          endYear = parseInt(yearMatch[1]) || 0;
        } else {
          endYear = parseInt(endDate) || 0;
        }
      }

      let duration = "";
      if (startYear && endYear) {
        const years = endYear - startYear;
        if (years > 0) {
          duration = years === 1 ? "1 year" : `${years} years`;
        } else {
          duration = "< 1 year";
        }
      }
      
      return {
        start: startDate,
        end: endDate,
        duration,
        isPresent: endDate === "PRESENT",
        startYear,
        endYear,
        years: endYear - startYear,
      };
    }
    
    return {
      start: cleanDate,
      end: "",
      duration: "",
      isPresent: false,
      startYear: 0,
      endYear: 0,
      years: 0,
    };
  };

  // Helper function to get company initial or icon
  const getCompanyInitial = (company: string) => {
    return company.charAt(0).toUpperCase();
  };

  // Helper function to parse skills string into array
  const parseSkills = (skills: string[] | string | undefined) => {
    if (!skills) return [];
    
    // If it's already an array, use it directly
    if (Array.isArray(skills)) {
      return skills
        .map(skill => skill.trim())
        .filter(skill => skill.length > 0)
        .slice(0, 8); // Limit to 8 skills to keep it dense
    }
    
    // If it's a string (from AI data), split by common delimiters and clean up
    if (typeof skills === 'string' && skills.trim() !== "") {
      return skills
        .split(/[,;|]/)
        .map(skill => skill.trim())
        .filter(skill => skill.length > 0)
        .slice(0, 8); // Limit to 8 skills to keep it dense
    }
    
    return [];
  };

  // Helper function to calculate years by job role from database experiences
  const calculateYearsByJob = () => {
    const jobYears: Record<string, number> = {};
    
    // Process all experiences (both database and locally added)
    experienceData.forEach(experience => {
      const role = experience.role?.trim();
      if (!role) return;
      
      const dateInfo = parseDateRange(experience.dates, experience);
      // Use the calculated years from database dates
      const years = dateInfo.years || Math.max((dateInfo.endYear - dateInfo.startYear) || 0, 0.1);
      
      // Accumulate years for this job role
      jobYears[role] = (jobYears[role] || 0) + years;
    });
    
    // Sort by years descending
    return Object.entries(jobYears)
      .sort(([,a], [,b]) => b - a);
  };

  // Helper function to calculate years by skills "dumbly" - for each skill, sum total experience years
  const calculateYearsBySkills = () => {
    const skillYears: Record<string, number> = {};
    
    // For each skill from database, calculate total years across ALL experiences
    dbSkills.forEach(skill => {
      const skillName = skill.name;
      let totalYears = 0;
      
      // Check all experiences to see where this skill was used
      experienceData.forEach(experience => {
        const expSkills = parseSkills(experience.skills);
        
        // If this experience has this skill, add its years
        if (expSkills.some(s => s.trim() === skillName)) {
          const dateInfo = parseDateRange(experience.dates, experience);
          const years = dateInfo.years || Math.max((dateInfo.endYear - dateInfo.startYear) || 0, 0.1);
          totalYears += years;
        }
      });
      
      if (totalYears > 0) {
        skillYears[skillName] = totalYears;
      }
    });
    
    // Sort by years descending
    return Object.entries(skillYears)
      .sort(([,a], [,b]) => b - a);
  };

  // Helper function to get skill breakdown details for popover from ALL experiences
  const getSkillBreakdown = (targetSkill: string) => {
    const breakdown: { role: string; company: string; dates: string; years: number; endYear: number; source: 'db' | 'added' }[] = [];
    
    // Process all experiences (both database and locally added)
    experienceData.forEach((experience, index) => {
      const skills = parseSkills(experience.skills);
      const dateInfo = parseDateRange(experience.dates, experience);
      // Use calculated years from database dates
      const years = dateInfo.years || Math.max((dateInfo.endYear - dateInfo.startYear) || 0, 0.1);
      const isLocallyAdded = experienceData[index].isLocallyAdded;
      
      if (skills.some(skill => skill.trim() === targetSkill)) {
        breakdown.push({
          role: experience.role,
          company: experience.company,
          dates: experience.dates,
          years,
          endYear: dateInfo.endYear,
          source: isLocallyAdded ? 'added' : 'db'
        });
      }
    });
    
    return breakdown.sort((a, b) => b.endYear - a.endYear); // Sort by most recent usage
  };

  // Helper function to check if a skill hasn't been used in over the threshold years
  const isSkillRecentlyUsed = (targetSkill: string) => {
    const currentYear = new Date().getFullYear();
    const skillBreakdown = getSkillBreakdown(targetSkill);
    
    if (skillBreakdown.length === 0) return true; // No data, assume it's recent
    
    // Check if any usage is within the threshold years
    const mostRecentUsage = Math.max(...skillBreakdown.map(exp => exp.endYear));
    return (currentYear - mostRecentUsage) <= outdatedThreshold;
  };

  // Helper function to check if a job position hasn't been used in over the threshold years
  const isJobRecentlyUsed = (targetJob: string) => {
    const currentYear = new Date().getFullYear();
    const jobBreakdown = getJobBreakdown(targetJob);
    
    if (jobBreakdown.length === 0) return true; // No data, assume it's recent
    
    // Check if any usage is within the threshold years
    const mostRecentUsage = Math.max(...jobBreakdown.map(exp => exp.endYear));
    return (currentYear - mostRecentUsage) <= outdatedThreshold;
  };

  // Helper function to get job role breakdown from database
  const getJobBreakdown = (targetJob: string) => {
    // Find experiences that match the job role
    const relatedExperiences = experienceData.filter(exp => 
      exp.role.toLowerCase().includes(targetJob.toLowerCase()) ||
      targetJob.toLowerCase().includes(exp.role.toLowerCase())
    );
    
    return relatedExperiences.map(exp => {
      const dateInfo = parseDateRange(exp.dates, exp);
      const years = dateInfo.years || Math.max((dateInfo.endYear - dateInfo.startYear) || 0, 0.1);
      return {
        role: exp.role,
        company: exp.company,
        dates: exp.dates,
        years,
        endYear: dateInfo.endYear
      };
    }).sort((a, b) => b.endYear - a.endYear); // Sort by most recent usage
  };

  // Show loading state while fetching database experiences
  if (dbLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading experience data...</p>
        </div>
      </div>
    );
  }

  if (!experienceData || experienceData.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <h3 className="text-base font-medium mb-1">No Experience Data</h3>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Experience information will appear here when available from the database.
          </p>
        </div>
      </div>
    );
  }

  // Sort experiences by relevance and recency (most recent first)
  const sortedExperiences = [...experienceData].sort((a, b) => {
    const dateA = parseDateRange(a.dates, a);
    const dateB = parseDateRange(b.dates, b);
    
    // Present positions come first
    if (dateA.isPresent && !dateB.isPresent) return -1;
    if (!dateA.isPresent && dateB.isPresent) return 1;
    
    // For both present positions, sort by start year (most recent start first)
    if (dateA.isPresent && dateB.isPresent) {
      return dateB.startYear - dateA.startYear;
    }
    
    // For both past positions, sort by end year first, then start year
    if (!dateA.isPresent && !dateB.isPresent) {
      if (dateA.endYear !== dateB.endYear) {
        return dateB.endYear - dateA.endYear; // Most recent end date first
      }
      
      // If end years are the same, sort by start year
      return dateB.startYear - dateA.startYear;
    }
    
    return 0;
  });

  const totalExperience = sortedExperiences.length;
  const currentRoles = sortedExperiences.filter(exp => parseDateRange(exp.dates, exp).isPresent).length;

  return (
    <Tabs defaultValue="timeline" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="timeline" className="flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          <span>Experience</span>
        </TabsTrigger>
        <TabsTrigger value="years" className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <span>Years of experience</span>
        </TabsTrigger>
        <TabsTrigger value="academic" className="flex items-center gap-2">
          <GraduationCap className="h-4 w-4" />
          <span>Academic</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="timeline" className="space-y-4">
        {/* Header with Add Button */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            Professional Experience
          </h3>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90 text-white flex items-center gap-2 px-4 py-2"
          >
            <Plus className="h-4 w-4" />
            Add Experience
          </Button>
        </div>

        {/* Header Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-blue-500 rounded-sm">
                  <Briefcase className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-500 font-medium">Positions</p>
                  <p className="text-lg font-bold text-blue-700">{totalExperience}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-green-500 rounded-sm">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium">Current</p>
                  <p className="text-lg font-bold text-green-700">{currentRoles}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-3">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-purple-500 rounded-sm">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium">Companies</p>
                  <p className="text-lg font-bold text-purple-700">
                    {new Set(sortedExperiences.map(exp => exp.company)).size}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Experience Timeline */}
        <div className="space-y-3">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

            {sortedExperiences.map((experience, index) => {
              const dateInfo = parseDateRange(experience.dates, experience);
              const isPresent = dateInfo.isPresent;
              const skills = parseSkills(experience.skills);

              const isLocallyAdded = experience.isLocallyAdded;

              return (
                <div key={index} className="relative flex items-start space-x-4 pb-4">
                  {/* Timeline dot */}
                  <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-sm border-2 text-white font-bold text-sm ${
                    isLocallyAdded
                      ? 'bg-gradient-to-br from-purple-400 to-purple-600 border-purple-600'
                      : isPresent 
                        ? 'bg-gradient-to-br from-green-400 to-green-600 border-green-600' 
                        : 'bg-gradient-to-br from-blue-400 to-blue-500 border-blue-500'
                  }`}>
                    {getCompanyInitial(experience.company)}
                  </div>

                  {/* Experience Card */}
                  <Card 
                    className={`flex-1 border-2 transition-colors duration-200 ${
                      isLocallyAdded 
                        ? 'border-purple-200 hover:border-purple-300' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                                {experience.role}
                              </h4>
                              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 mt-0.5">
                                <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="font-medium text-sm truncate">{experience.company}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {isPresent && (
                                <Badge variant="default" className="bg-green-100 text-green-700 border-green-300 text-xs px-2 py-0.5">
                                  Current
                                </Badge>
                              )}
                              {isLocallyAdded && (
                                <>
                                  <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-2 py-0.5">
                                    Added
                                  </Badge>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditExperience(experience, index);
                                    }}
                                    className="p-1 rounded-sm bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors duration-200"
                                    title="Edit experience"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfirmDelete(experience);
                                    }}
                                    className="p-1 rounded-sm bg-red-100 hover:bg-red-200 text-red-700 transition-colors duration-200"
                                    title="Delete experience"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400 mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{dateInfo.start}</span>
                              <span>-</span>
                              <span className={isPresent ? "text-green-600 font-medium" : ""}>
                                {dateInfo.end}
                              </span>
                            </div>
                            
                            {dateInfo.duration && (
                              <>
                                <Separator orientation="vertical" className="h-3" />
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  <span className="font-medium">{dateInfo.duration}</span>
                                </div>
                              </>
                            )}



                          </div>

                          {/* Skills Section */}
                          {skills.length > 0 && (
                            <div className="mt-2">
                              <div className="flex items-center gap-1.5 mb-2">
                                <Zap className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Skills & Tools</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {skills.map((skill, skillIndex) => (
                                  <Badge 
                                    key={skillIndex} 
                                    variant="secondary" 
                                    className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs px-2 py-0.5 font-normal"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="years" className="space-y-6">
        {/* Years Experience by Job */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              Years Experience by Job
            </h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="jobsThreshold" className="text-xs text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                  Outdated after:
                </label>
                <Input
                  id="jobsThreshold"
                  type="number"
                  min="1"
                  max="20"
                  value={outdatedThreshold}
                  onChange={(e) => setOutdatedThresholdValue(parseInt(e.target.value) || 3)}
                  className="w-16 h-8 text-center"
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">years</span>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Filter jobs..."
                  value={jobsFilter}
                  onChange={(e) => setJobsFilter(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            </div>
          </div>
          
          {(() => {
            const jobsYears = calculateYearsByJob();
            const filteredJobs = filterItems(jobsYears, jobsFilter);
            return filteredJobs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredJobs.map(([job, years]) => {
                  const jobBreakdown = getJobBreakdown(job);
                  const isRecent = isJobRecentlyUsed(job);
                  const currentYear = new Date().getFullYear();
                  const mostRecentUsage = jobBreakdown.length > 0 ? Math.max(...jobBreakdown.map(exp => exp.endYear)) : currentYear;
                  
                  // Create a proper date string for the most recent usage
                  const mostRecentDate = mostRecentUsage ? `${mostRecentUsage}-12-31` : null;
                  const lastUsedFormatted = formatExperienceLastUsed(mostRecentDate);
                  
                  return (
                    <Popover key={job}>
                      <PopoverTrigger asChild>
                        <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors duration-200 cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{job}</h4>
                                  <Info className="h-3.5 w-3.5 text-gray-400" />
                                  {!isRecent && (
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                                  )}
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Clock className="h-3.5 w-3.5 text-gray-500" />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {years.toFixed(1)} {years === 1 ? 'year' : 'years'}
                                  </span>
                                  {!isRecent && (
                                    <>
                                      <Separator orientation="vertical" className="h-3" />
                                      <span className="text-xs text-amber-600 font-medium">
                                        Last used {lastUsedFormatted}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className={`text-2xl font-bold ${isRecent ? 'text-blue-500' : 'text-amber-600'}`}>
                                  {years.toFixed(1)}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-blue-500" />
                            <h4 className="font-semibold text-gray-900">{job} Experience Breakdown</h4>
                            {!isRecent && (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                          
                          {!isRecent && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Position may be outdated</span>
                              </div>
                              <p className="text-xs text-amber-600">
                                This job position hasn't been used in {lastUsedFormatted} (threshold: {outdatedThreshold}+ years). Consider if it's still relevant for current opportunities.
                              </p>
                            </div>
                          )}
                          
                          {jobBreakdown.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-sm text-gray-600 mb-3">
                                Based on related positions in experience:
                              </p>
                              {jobBreakdown.map((exp, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 rounded-sm">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 truncate">{exp.role}</p>
                                    <p className="text-xs text-gray-600">{exp.company}</p>
                                    <p className="text-xs text-gray-500">{exp.dates}</p>
                                  </div>
                                  <div className="ml-2">
                                    <span className="text-sm font-bold text-blue-500">
                                      {exp.years.toFixed(1)}y
                                    </span>
                                  </div>
                                </div>
                              ))}
                              <div className="border-t pt-2 mt-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700">Total:</span>
                                  <span className="text-lg font-bold text-blue-500">{years.toFixed(1)} years</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <BarChart3 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm text-gray-500">
                                Total experience calculated from all positions with this role.
                              </p>
                              <div className="mt-2 p-2 bg-blue-50 rounded-sm">
                                <span className="text-lg font-bold text-blue-500">{years.toFixed(1)} years</span>
                                <p className="text-xs text-gray-600">Total {job} experience</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </div>
            ) : (
              <Card className="border-2 border-dashed border-gray-300">
                <CardContent className="p-6 text-center">
                  <Briefcase className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    {jobsYears.length === 0 ? "No job experience data available" : `No jobs match "${jobsFilter}"`}
                  </p>
                  {jobsYears.length > 0 && jobsFilter && (
                    <button
                      onClick={() => setJobsFilter("")}
                      className="text-xs text-blue-500 hover:text-blue-800 mt-2"
                    >
                      Clear filter
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Years by Skills */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Zap className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              Years by Skills
            </h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="skillsThreshold" className="text-xs text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                  Outdated after:
                </label>
                <Input
                  id="skillsThreshold"
                  type="number"
                  min="1"
                  max="20"
                  value={outdatedThreshold}
                  onChange={(e) => setOutdatedThresholdValue(parseInt(e.target.value) || 3)}
                  className="w-16 h-8 text-sm text-center"
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">years</span>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Filter skills..."
                  value={skillsFilter}
                  onChange={(e) => setSkillsFilter(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            </div>
          </div>
          
          {(() => {
            const skillsYears = calculateYearsBySkills();
            const filteredSkills = filterItems(skillsYears, skillsFilter);
            return filteredSkills.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredSkills.map(([skill, years]) => {
                  const skillBreakdown = getSkillBreakdown(skill);
                  const isRecent = isSkillRecentlyUsed(skill);
                  const currentYear = new Date().getFullYear();
                  const mostRecentUsage = skillBreakdown.length > 0 ? Math.max(...skillBreakdown.map(exp => exp.endYear)) : currentYear;
                  
                  // Create a proper date string for the most recent usage
                  const mostRecentDate = mostRecentUsage ? `${mostRecentUsage}-12-31` : null;
                  const lastUsedFormatted = formatExperienceLastUsed(mostRecentDate);
                  
                  return (
                    <Popover key={skill}>
                      <PopoverTrigger asChild>
                        <Card className="border-2 border-green-200 hover:border-green-300 transition-colors duration-200 cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{skill}</h4>
                                  <Info className="h-3.5 w-3.5 text-gray-400" />
                                  {!isRecent && (
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                                  )}
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Clock className="h-3.5 w-3.5 text-gray-500" />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {years.toFixed(1)} {years === 1 ? 'year' : 'years'}
                                  </span>
                                  {!isRecent && (
                                    <>
                                      <Separator orientation="vertical" className="h-3" />
                                      <span className="text-xs text-amber-600 font-medium">
                                        Last used {lastUsedFormatted}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className={`text-2xl font-bold ${isRecent ? 'text-green-600' : 'text-amber-600'}`}>
                                  {years.toFixed(1)}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-green-600" />
                            <h4 className="font-semibold text-gray-900">{skill} Experience Breakdown</h4>
                            {!isRecent && (
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                          
                          {!isRecent && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Skill may be outdated</span>
                              </div>
                              <p className="text-xs text-amber-600">
                                This skill hasn't been used in {lastUsedFormatted} (threshold: {outdatedThreshold}+ years). Consider if it's still relevant for current opportunities.
                              </p>
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 mb-3">
                              Used in the following positions:
                            </p>
                            {skillBreakdown.map((exp, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-sm">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm text-gray-900 truncate">{exp.role}</p>
                                    {exp.source === 'added' && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                        Added
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600">{exp.company}</p>
                                  <p className="text-xs text-gray-500">{exp.dates}</p>
                                </div>
                                <div className="ml-2">
                                  <span className="text-sm font-bold text-green-600">
                                    {exp.years.toFixed(1)}y
                                  </span>
                                </div>
                              </div>
                            ))}
                            <div className="border-t pt-2 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Total Experience:</span>
                                <span className="text-lg font-bold text-green-600">{years.toFixed(1)} years</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </div>
            ) : (
              <Card className="border-2 border-dashed border-gray-300">
                <CardContent className="p-6 text-center">
                  <Zap className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    {skillsYears.length === 0 ? "No skills data available" : `No skills match "${skillsFilter}"`}
                  </p>
                  {skillsYears.length > 0 && skillsFilter && (
                    <button
                      onClick={() => setSkillsFilter("")}
                      className="text-xs text-blue-500 hover:text-blue-800 mt-2"
                    >
                      Clear filter
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </TabsContent>

      <TabsContent value="academic" className="space-y-6">
        {/* Academic Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            Academic Background
          </h3>
          
          {(() => {
            const educationData = aiAnalysis?.education_level?.["Extracted Value"] || aiAnalysis?.education_level;
            return educationData && (
              (Array.isArray(educationData) && educationData.length > 0) ||
              (!Array.isArray(educationData) && educationData.degree)
            );
          })() ? (
            <div className="space-y-6">
              {/* Education Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Total Degrees Card */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-purple-500 rounded-sm">
                        <GraduationCap className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Total Degrees</p>
                        <p className="text-lg font-bold text-purple-700">
                          {(() => {
                            const educationData = aiAnalysis?.education_level?.["Extracted Value"] || aiAnalysis?.education_level;
                            return Array.isArray(educationData) ? educationData.length : 1;
                          })()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Latest Degree Card */}
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-green-500 rounded-sm">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-green-600 font-medium">Latest</p>
                        <p className="text-lg font-bold text-green-700 truncate">
                          {(() => {
                            const educationData = aiAnalysis?.education_level?.["Extracted Value"] || aiAnalysis?.education_level;
                            const educationArray = Array.isArray(educationData) ? educationData : [educationData];
                            return educationArray
                              .sort((a, b) => {
                                if (!a.year || a.year === "Not explicitly stated") {
                                  if (!b.year || b.year === "Not explicitly stated") return 0;
                                  return 1;
                                }
                                if (!b.year || b.year === "Not explicitly stated") return -1;
                                return parseInt(b.year) - parseInt(a.year);
                              })[0]?.degree?.split(' ')[0] || 'N/A';
                          })()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Institutions Card */}
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-3">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-blue-500 rounded-sm">
                        <Building2 className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-500 font-medium">Institutions</p>
                        <p className="text-lg font-bold text-blue-700">
                          {(() => {
                            const educationData = aiAnalysis?.education_level?.["Extracted Value"] || aiAnalysis?.education_level;
                            const educationArray = Array.isArray(educationData) ? educationData : [educationData];
                            return new Set(educationArray
                              .filter(ed => ed.institution)
                              .map(ed => ed.institution)).size || 'N/A';
                          })()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Education Timeline */}
              <div className="space-y-4">
                {/* Timeline line */}
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                {(() => {
                  const educationData = aiAnalysis?.education_level?.["Extracted Value"] || aiAnalysis?.education_level;
                  return Array.isArray(educationData) ? educationData : [educationData];
                })()
                  .sort((a, b) => {
                    // Sort by year descending (most recent first), put entries without year at the end
                    if (!a.year || a.year === "Not explicitly stated") {
                      if (!b.year || b.year === "Not explicitly stated") return 0;
                      return 1;
                    }
                    if (!b.year || b.year === "Not explicitly stated") return -1;
                    return parseInt(b.year) - parseInt(a.year);
                  })
                  .map((education, index) => (
                    <div key={index} className="relative flex items-start space-x-4 pb-6">
                      {/* Timeline dot */}
                      <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-sm border-2 bg-gradient-to-br from-blue-400 to-blue-500 border-blue-500 text-white">
                        <GraduationCap className="h-6 w-6" />
                      </div>

                      {/* Education Card */}
                      <Card className="flex-1 border-2 border-blue-200 hover:border-blue-300 transition-colors duration-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                                    {education.degree}
                                  </h4>
                                  {education.institution && (
                                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 mt-0.5">
                                      <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                      <span className="font-medium text-sm truncate">{education.institution}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {education.year && education.year !== "Not explicitly stated" && (
                                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span className="font-medium">Graduated {education.year}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
                

              </div>
            </div>
          ) : (
            <Card className="border-2 border-dashed border-gray-300">
              <CardContent className="p-8 text-center">
                <GraduationCap className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <h3 className="text-base font-medium mb-1 text-gray-500 dark:text-gray-400">No Academic Data</h3>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Educational background information will appear here when available from AI analysis.
                </p>
              </CardContent>
            </Card>
          )}
        </div>


      </TabsContent>

      {/* Add Experience Modal */}
      <AddExperienceModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddExperience}
        isLoading={isLoading}
        editingExperience={editingExperience}
        editingIndex={editingIndex}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("experiences.deleteConfirmTitle", "Delete Experience")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("experiences.deleteConfirmDescription", "Are you sure you want to delete this experience entry? This action cannot be undone.")}
              {experienceToDelete && (
                <div className="mt-2 p-2 bg-gray-50 rounded border text-sm">
                  <strong>{experienceToDelete.role}</strong> at {experienceToDelete.company}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExperience}
              className="bg-red-600 hover:bg-red-700"
              disabled={isLoading}
            >
              {isLoading ? t("common.deleting", "Deleting...") : t("common.delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
};
